import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useImperativeHandle, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Hls } from "../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { ContentMetadataText } from "./common/ContentMetadataText.js";
import AlertCircle from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/alert-circle.js";
import RefreshCw from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/refresh-cw.js";
import Loader2 from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/loader-2.js";
import Play from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/play.js";
import Pause from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/pause.js";
import RotateCcw from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/rotate-ccw.js";
import VolumeX from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/volume-x.js";
import Volume2 from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/volume-2.js";
import Minimize from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/minimize.js";
import Maximize from "../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/maximize.js";
const QueturePlayer = forwardRef(function QueturePlayer2({
  qid,
  thumbnailUrl,
  title,
  renderMetadataMath = false,
  onComplete,
  onProgress,
  autoPlay = false,
  quetureApi,
  allowSkip = true,
  requiredWatchPercent = 90,
  hlsUrl: directHlsUrl,
  // Direct HLS URL from Learning Session API
  startTime = 0,
  // Start time in seconds (for resume from last position)
  componentProgressId: _componentProgressId,
  // Reserved for future use
  onHeartbeat,
  onVideoProgress,
  onSkipBlocked,
  externalControls = false,
  onPlayStateChange,
  onPlaybackRateChange
}, ref) {
  const { t } = useTranslation("player");
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [maxProgress, setMaxProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [quetureData, setQuetureData] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const lastHeartbeatTimeRef = useRef(0);
  const heartbeatStartTimeRef = useRef(0);
  const wasSeekingRef = useRef(false);
  const playbackSpeedRef = useRef(1);
  useImperativeHandle(ref, () => ({
    pause: () => {
      var _a;
      (_a = videoRef.current) == null ? void 0 : _a.pause();
    },
    play: () => {
      var _a;
      (_a = videoRef.current) == null ? void 0 : _a.play().catch(() => {
      });
    },
    seek: (seconds) => {
      if (videoRef.current) videoRef.current.currentTime = seconds;
    },
    flushHeartbeat: () => {
      const video = videoRef.current;
      if (!video || !onHeartbeat || lastHeartbeatTimeRef.current <= 0) return;
      onHeartbeat({
        startTimeSec: heartbeatStartTimeRef.current || video.currentTime,
        endTimeSec: video.currentTime,
        playbackSpeed: playbackSpeedRef.current,
        wasSeeking: wasSeekingRef.current
      });
      lastHeartbeatTimeRef.current = Date.now();
      heartbeatStartTimeRef.current = video.currentTime;
      wasSeekingRef.current = false;
    },
    getCurrentTime: () => {
      var _a;
      return ((_a = videoRef.current) == null ? void 0 : _a.currentTime) ?? 0;
    },
    getMaxProgressSec: () => {
      return maxProgress / 100 * duration;
    },
    setPlaybackRate: (rate) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = rate;
        setPlaybackRate(rate);
        playbackSpeedRef.current = rate;
        onPlaybackRateChange == null ? void 0 : onPlaybackRateChange(rate);
      }
    },
    isPaused: () => {
      var _a;
      return ((_a = videoRef.current) == null ? void 0 : _a.paused) ?? true;
    },
    getDuration: () => {
      var _a;
      return ((_a = videoRef.current) == null ? void 0 : _a.duration) ?? 0;
    },
    setMuted: (muted) => {
      if (videoRef.current) {
        videoRef.current.muted = muted;
        setIsMuted(muted);
      }
    },
    setVolume: (volume) => {
      if (videoRef.current) {
        videoRef.current.volume = Math.max(0, Math.min(1, volume));
      }
    }
  }), [maxProgress, duration, onHeartbeat, onPlaybackRateChange]);
  const controlsTimeoutRef = useRef(null);
  useEffect(() => {
    if (directHlsUrl) {
      setQuetureData({
        qid,
        hlsUrl: directHlsUrl,
        thumbnailUrl: thumbnailUrl || null,
        title: title || null
      });
      setIsLoading(false);
      return;
    }
    if (!qid) return;
    const fetchQuetureData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const quetureInfo = await quetureApi.getQuetureInfo(qid);
        if (quetureInfo.hlsUrl) {
          setQuetureData(quetureInfo);
        } else {
          setError(t("video.urlNotFound"));
        }
      } catch (err) {
        console.error("Failed to fetch queture data:", err);
        setError(t("video.loadError"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuetureData();
  }, [qid, directHlsUrl, thumbnailUrl, title, quetureApi]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !(quetureData == null ? void 0 : quetureData.hlsUrl)) return;
    const initPlayer = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          xhrSetup: (xhr) => {
            xhr.withCredentials = false;
          }
        });
        hls.loadSource(quetureData.hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          if (startTime > 0 && video.duration > 0) {
            video.currentTime = Math.min(startTime, video.duration - 1);
          }
          if (autoPlay) {
            video.play().catch(console.error);
          }
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          console.error("HLS error:", data.type, data.details);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError(t("video.networkError"));
                setTimeout(() => {
                  hls.startLoad();
                  setError(null);
                }, 1e3);
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                setError(t("video.cannotPlay"));
                hls.destroy();
                break;
            }
          }
        });
        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = quetureData.hlsUrl;
        video.addEventListener("loadedmetadata", () => {
          setIsLoading(false);
          if (startTime > 0 && video.duration > 0) {
            video.currentTime = Math.min(startTime, video.duration - 1);
          }
          if (autoPlay) {
            video.play().catch(console.error);
          }
        });
      } else {
        setError(t("video.hlsNotSupported"));
      }
    };
    initPlayer();
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [quetureData == null ? void 0 : quetureData.hlsUrl, autoPlay]);
  const startTimeSetRef = useRef(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    startTimeSetRef.current = false;
    const handleTimeUpdate = () => {
      const currentProgress = video.currentTime / video.duration * 100 || 0;
      setCurrentTime(video.currentTime);
      setProgress(currentProgress);
      setMaxProgress((prev) => Math.max(prev, currentProgress));
      onProgress == null ? void 0 : onProgress(currentProgress, video.currentTime, video.duration);
      if (onVideoProgress) {
        onVideoProgress({
          currentPositionSec: video.currentTime,
          progressPercent: currentProgress
        });
      }
      if (onHeartbeat && !video.paused) {
        const now = Date.now();
        const timeSinceLastHeartbeat = (now - lastHeartbeatTimeRef.current) / 1e3;
        if (timeSinceLastHeartbeat >= 5) {
          const startTime2 = heartbeatStartTimeRef.current || video.currentTime;
          const endTime = video.currentTime;
          onHeartbeat({
            startTimeSec: startTime2,
            endTimeSec: endTime,
            playbackSpeed: playbackSpeedRef.current,
            wasSeeking: wasSeekingRef.current
          });
          lastHeartbeatTimeRef.current = now;
          heartbeatStartTimeRef.current = endTime;
          wasSeekingRef.current = false;
        }
      }
    };
    const handleDurationChange = () => {
      setDuration(video.duration);
    };
    const handleLoadedMetadata = () => {
      if (startTime > 0 && !startTimeSetRef.current && video.duration > 0) {
        video.currentTime = Math.min(startTime, video.duration - 1);
        startTimeSetRef.current = true;
        console.debug(`[QueturePlayer] Set start time to ${startTime}s (duration: ${video.duration}s)`);
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      onPlayStateChange == null ? void 0 : onPlayStateChange(true);
      if (onHeartbeat) {
        const now = Date.now();
        lastHeartbeatTimeRef.current = now;
        heartbeatStartTimeRef.current = video.currentTime;
      }
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange == null ? void 0 : onPlayStateChange(false);
      if (onHeartbeat && lastHeartbeatTimeRef.current > 0) {
        const startTime2 = heartbeatStartTimeRef.current || video.currentTime;
        const endTime = video.currentTime;
        onHeartbeat({
          startTimeSec: startTime2,
          endTimeSec: endTime,
          playbackSpeed: playbackSpeedRef.current,
          wasSeeking: wasSeekingRef.current
        });
        wasSeekingRef.current = false;
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      if (maxProgress >= requiredWatchPercent) {
        onComplete == null ? void 0 : onComplete();
      }
    };
    const handleSeeking = () => {
      wasSeekingRef.current = true;
    };
    const handleSeeked = () => {
      if (onHeartbeat) {
        heartbeatStartTimeRef.current = video.currentTime;
        lastHeartbeatTimeRef.current = Date.now();
      }
    };
    const handleRateChange = () => {
      playbackSpeedRef.current = video.playbackRate;
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleError = (e) => {
      console.error("Video error:", e);
      setError(t("video.cannotPlay"));
    };
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("ratechange", handleRateChange);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("ratechange", handleRateChange);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);
    };
  }, [onComplete, onProgress, onVideoProgress, onHeartbeat, onPlayStateChange, maxProgress, requiredWatchPercent, startTime]);
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3e3);
    }
  };
  const formatTime = (time) => {
    if (!time || isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(!isMuted);
  };
  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen().catch(console.error);
    }
  };
  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const progressBar = progressBarRef.current;
    if (!video || !progressBar) return;
    if (!allowSkip) {
      const rect2 = progressBar.getBoundingClientRect();
      const pos2 = (e.clientX - rect2.left) / rect2.width;
      const targetTime = pos2 * video.duration;
      if (targetTime > maxProgress / 100 * video.duration) {
        onSkipBlocked == null ? void 0 : onSkipBlocked();
        return;
      }
    }
    const rect = progressBar.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * video.duration;
  };
  const replay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().catch(console.error);
  };
  const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const changePlaybackRate = (rate) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
    playbackSpeedRef.current = rate;
    setShowSpeedMenu(false);
    onPlaybackRateChange == null ? void 0 : onPlaybackRateChange(rate);
  };
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    if (directHlsUrl) {
      setQuetureData({
        qid,
        hlsUrl: directHlsUrl,
        thumbnailUrl: thumbnailUrl || null,
        title: title || null
      });
      setIsLoading(false);
      return;
    }
    quetureApi.getQuetureInfo(qid).then((quetureInfo) => {
      if (quetureInfo.hlsUrl) {
        setQuetureData(quetureInfo);
      } else {
        setError(t("video.urlNotFound"));
      }
    }).catch((err) => {
      console.error("Retry failed:", err);
      setError(t("video.loadError"));
    }).finally(() => {
      setIsLoading(false);
    });
  };
  const displayThumbnail = (quetureData == null ? void 0 : quetureData.thumbnailUrl) || thumbnailUrl;
  const displayTitle = (quetureData == null ? void 0 : quetureData.title) || title;
  if (!qid || qid.trim() === "") {
    return /* @__PURE__ */ jsx("div", { className: "flex justify-center items-center bg-linear-to-br from-gray-900 to-gray-800 rounded-xl aspect-video", children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertCircle, { className: "mx-auto mb-3 w-12 h-12 text-yellow-400" }),
      /* @__PURE__ */ jsx("p", { className: "mb-2 text-white/80", children: t("video.noIdProvided") }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-white/50", children: t("video.cannotLoadInfo") })
    ] }) });
  }
  if (error && !(quetureData == null ? void 0 : quetureData.hlsUrl)) {
    return /* @__PURE__ */ jsx("div", { className: "flex justify-center items-center bg-linear-to-br from-gray-900 to-gray-800 rounded-xl aspect-video", children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
      /* @__PURE__ */ jsx(AlertCircle, { className: "mx-auto mb-3 w-12 h-12 text-red-400" }),
      /* @__PURE__ */ jsx("p", { className: "mb-2 text-white/80", children: error }),
      /* @__PURE__ */ jsxs(
        "button",
        {
          onClick: handleRetry,
          className: "mt-4 inline-flex gap-2 items-center px-4 py-2 text-white rounded-lg transition-colors bg-white/10 hover:bg-white/20",
          children: [
            /* @__PURE__ */ jsx(RefreshCw, { className: "w-4 h-4" }),
            t("video.retry")
          ]
        }
      )
    ] }) });
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      className: "overflow-hidden relative bg-black rounded-xl aspect-video group",
      onMouseMove: resetControlsTimeout,
      onMouseLeave: () => isPlaying && setShowControls(false),
      children: [
        /* @__PURE__ */ jsx(
          "video",
          {
            ref: videoRef,
            className: "w-full h-full",
            poster: displayThumbnail || void 0,
            playsInline: true,
            onClick: togglePlay,
            controlsList: "nodownload"
          }
        ),
        isLoading && /* @__PURE__ */ jsx("div", { className: "flex absolute inset-0 justify-center items-center bg-black/50", children: /* @__PURE__ */ jsx(Loader2, { className: "w-12 h-12 text-white animate-spin" }) }),
        !externalControls && !isPlaying && !isLoading && (quetureData == null ? void 0 : quetureData.hlsUrl) && /* @__PURE__ */ jsx(
          "div",
          {
            className: "flex absolute inset-0 justify-center items-center cursor-pointer bg-black/30",
            onClick: togglePlay,
            children: /* @__PURE__ */ jsx("div", { className: "flex justify-center items-center w-20 h-20 rounded-full backdrop-blur-xs transition-colors bg-white/20 hover:bg-white/30", children: /* @__PURE__ */ jsx(Play, { className: "ml-1 w-10 h-10 text-white" }) })
          }
        ),
        !externalControls && /* @__PURE__ */ jsxs(
          "div",
          {
            className: `absolute bottom-0 left-0 right-0 z-20 bg-linear-to-t from-black/90 via-black/70 to-transparent p-4 transition-opacity duration-300 ${showControls || !isPlaying ? "opacity-100" : "opacity-0"}`,
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  ref: progressBarRef,
                  className: "mb-3 cursor-pointer group/progress",
                  onClick: handleProgressClick,
                  children: /* @__PURE__ */ jsx("div", { className: "w-full h-2 rounded-full border shadow-lg backdrop-blur-xs bg-white/20 border-white/10", children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "relative h-full bg-indigo-500 rounded-full shadow-md",
                      style: { width: `${Math.max(progress, 0)}%` },
                      children: /* @__PURE__ */ jsx("div", { className: "absolute right-0 top-1/2 w-4 h-4 bg-white rounded-full border-2 border-indigo-500 shadow-lg opacity-0 transition-opacity -translate-y-1/2 group-hover/progress:opacity-100" })
                    }
                  ) })
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex gap-3 items-center", children: [
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: togglePlay,
                      className: "p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl",
                      children: isPlaying ? /* @__PURE__ */ jsx(Pause, { className: "w-5 h-5" }) : /* @__PURE__ */ jsx(Play, { className: "w-5 h-5 ml-0.5" })
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: replay,
                      className: "p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl",
                      children: /* @__PURE__ */ jsx(RotateCcw, { className: "w-4 h-4" })
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: toggleMute,
                      className: "p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl",
                      children: isMuted ? /* @__PURE__ */ jsx(VolumeX, { className: "w-5 h-5" }) : /* @__PURE__ */ jsx(Volume2, { className: "w-5 h-5" })
                    }
                  ),
                  /* @__PURE__ */ jsxs("span", { className: "px-2 py-1 text-sm font-medium text-white rounded-xs shadow-md backdrop-blur-xs bg-black/50", children: [
                    formatTime(currentTime),
                    " / ",
                    formatTime(duration)
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex gap-3 items-center", children: [
                  /* @__PURE__ */ jsxs("div", { className: "relative", children: [
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: () => setShowSpeedMenu(!showSpeedMenu),
                        className: "px-2 py-1.5 text-sm font-medium text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl min-w-[52px]",
                        children: playbackRate === 1 ? "1x" : `${playbackRate}x`
                      }
                    ),
                    showSpeedMenu && /* @__PURE__ */ jsx("div", { className: "absolute right-0 bottom-full mb-2 py-1 bg-black/90 backdrop-blur-xs rounded-lg shadow-xl border border-white/10 min-w-[80px]", children: PLAYBACK_RATES.map((rate) => /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: () => changePlaybackRate(rate),
                        className: `w-full px-3 py-1.5 text-sm text-left transition-colors ${playbackRate === rate ? "text-indigo-400 bg-white/10" : "text-white hover:bg-white/10"}`,
                        children: rate === 1 ? t("video.normal") : `${rate}x`
                      },
                      rate
                    )) })
                  ] }),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: toggleFullscreen,
                      className: "p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl",
                      children: isFullscreen ? /* @__PURE__ */ jsx(Minimize, { className: "w-5 h-5" }) : /* @__PURE__ */ jsx(Maximize, { className: "w-5 h-5" })
                    }
                  )
                ] })
              ] })
            ]
          }
        ),
        !externalControls && displayTitle && showControls && /* @__PURE__ */ jsx("div", { className: "absolute top-0 right-0 left-0 p-4 bg-linear-to-b to-transparent from-black/60", children: /* @__PURE__ */ jsx("h3", { className: "font-medium text-white truncate", children: renderMetadataMath ? /* @__PURE__ */ jsx(ContentMetadataText, { text: displayTitle }) : displayTitle }) })
      ]
    }
  );
});
export {
  QueturePlayer,
  QueturePlayer as default
};
//# sourceMappingURL=QueturePlayer.js.map
